import { Button, Radio, RadioGroup } from '@ads/components-react';
import { ExclamationTriangle } from '@ads/icons';
import { zodResolver } from '@hookform/resolvers/zod';
import { eventDashboardMutations } from '@linka/http/event-dashboard';
import { useTranslation } from '@linka/i18n';
import { useMutation } from '@tanstack/react-query';
import { useAtomValue, useSetAtom } from 'jotai/react';
import { useForm } from 'react-hook-form';
import z from 'zod';
import {
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../../../dialog/dialog';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '../../../../form/form';
import { toast } from '../../../../toast/toast';
import { buildExportPayload } from './build-export-payload';
import { useExportContext } from './context';
import { exportChartAtom, exportDataAtom } from './state';

export const ExportConfigForm = () => {
  const t = useTranslation();
  const { eventId, filters } = useExportContext();
  const exportChartType = useAtomValue(exportChartAtom);
  const setExportData = useSetAtom(exportDataAtom);

  const exportChartSchema = z.object({
    filters: z.enum(['yes', 'no'], {
      message: t('export-flow.error-required'),
    }),
    format: z.enum(['CSV', 'XLSX'], {
      message: t('export-flow.error-required'),
    }),
  });

  const form = useForm({
    resolver: zodResolver(exportChartSchema),
  });

  const { mutate } = useMutation(eventDashboardMutations.requestDownload(eventId));

  const onSubmit = (data: z.infer<typeof exportChartSchema>) => {
    if (!exportChartType) return;

    const payload = buildExportPayload({
      reportType: exportChartType,
      format: data.format,
      applyFilters: data.filters === 'yes',
      filters,
    });

    mutate(payload, {
      onSuccess: ({ data }) => {
        setExportData({ exportId: data.exportId, status: data.status });
      },
      onError: ({ name, message }) => {
        toast({ title: name, description: message, status: 'danger' });
      },
    });
  };

  return (
    <DialogContent>
      <DialogHeader>
        <DialogClose />
        <DialogTitle>{t('export-flow.dialog.config.title')}</DialogTitle>
        <DialogDescription>{t('export-flow.dialog.config.description')}</DialogDescription>
      </DialogHeader>
      <div className='pt-xxs'>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className='flex flex-col gap-xxs w-fit'>
            <div>
              <FormField
                control={form.control}
                name='filters'
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className='mb-quark ts-subtitle-xxs text-ctx-content-title'>
                      {t('export-flow.filters')}
                    </FormLabel>
                    <FormControl>
                      <RadioGroup
                        name='filters'
                        className='flex flex-col gap-nano'
                        onValueChange={field.onChange}
                        defaultValue={field.value}
                        onBlur={field.onBlur}
                        value={field.value}
                      >
                        <Radio size='md' value='no' label={t('export-flow.export-all-data')} />
                        <Radio size='md' value='yes' label={t('export-flow.export-filtered-data')} />
                      </RadioGroup>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            <div>
              <FormField
                control={form.control}
                name='format'
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className='mb-quark ts-subtitle-xxs text-ctx-content-title'>
                      {t('export-flow.file-format')}
                    </FormLabel>
                    <FormControl>
                      <RadioGroup
                        name='format'
                        className='flex flex-col gap-nano'
                        onValueChange={field.onChange}
                        defaultValue={field.value}
                        onBlur={field.onBlur}
                        value={field.value}
                      >
                        <Radio size='md' value='XLSX' label='.xlsx' />
                        <Radio size='md' value='CSV' label='.csv' />
                      </RadioGroup>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            <div className='flex items-center gap-micro mt-xxs p-micro rounded-sm bg-base-warning-100 text-base-warning-700'>
              <div className='grid place-items-center size-xs shrink-0'>
                <ExclamationTriangle size={20} />
              </div>
              <p>{t('export-flow.dialog.config.alert')}</p>
            </div>
            <DialogFooter className='flex items-center justify-end gap-nano w-full'>
              <DialogClose asChild>
                <Button type='button' size='lg' hierarchy='secondary' className='!w-full sm:!w-auto'>
                  {t('export-flow.cancel')}
                </Button>
              </DialogClose>
              <Button type='submit' size='lg' className='!w-full sm:!w-auto'>
                {t('export-flow.export')}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </div>
    </DialogContent>
  );
};
